import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';

import { assistantsService, type Journal } from '../../services/api/assistants';

/**
 * Suivi de l'usage et de la dépense, pour l'administrateur.
 *
 * Répond à deux demandes distinctes du client : « savoir qui s'est connecté et
 * à quelle heure », et maîtriser la consommation. Les traces et les compteurs
 * de jetons étaient déjà enregistrés à chaque appel ; c'est leur lecture qui
 * manquait.
 *
 * Les montants sont affichés en dollars plutôt qu'en jetons : c'est le chiffre
 * sur lequel un chef de service peut décider d'un plafond.
 */

/** Encadré de section, repris à l'identique sur les quatre tableaux. */
const Section: React.FC<{
  titre: string;
  precision?: string;
  children: React.ReactNode;
}> = ({ titre, precision, children }) => (
  <section className="overflow-hidden rounded-md border border-[#DDE2DD] bg-eg-blanc">
    <header className="flex items-baseline gap-2.5 border-b border-eg-bordure px-3 py-2.5">
      <h2 className="text-[13px] font-semibold text-eg-encre">{titre}</h2>
      {precision && <span className="text-[11px] text-eg-gris">{precision}</span>}
    </header>
    {children}
  </section>
);

const enTete =
  'bg-eg-papier text-[10px] font-semibold uppercase tracking-[0.04em] text-eg-gris';

const SuiviUtilisation: React.FC = () => {
  const [journal, setJournal] = useState<Journal | null>(null);
  const [erreur, setErreur] = useState('');

  useEffect(() => {
    assistantsService
      .chargerJournal()
      .then(setJournal)
      .catch((e) => setErreur(e.message || 'Chargement impossible.'));
  }, []);

  if (erreur) {
    return (
      <p className="rounded-md border border-eg-accent/30 bg-eg-accent/5 px-4 py-3 text-sm text-eg-accent">
        {erreur}
      </p>
    );
  }

  if (!journal) {
    return (
      <div className="flex items-center gap-2 py-12 text-sm text-eg-gris">
        <Loader2 className="h-4 w-4 animate-spin" />
        Chargement du suivi...
      </div>
    );
  }

  const { synthese, utilisateurs, assistants, traces } = journal;

  const dateLisible = (valeur?: string | null) =>
    valeur
      ? new Date(valeur).toLocaleString('fr-FR', {
          day: '2-digit', month: '2-digit', year: 'numeric',
          hour: '2-digit', minute: '2-digit',
        })
      : '—';

  const moyenne = synthese.generations
    ? (synthese.cout_total / synthese.generations).toFixed(2)
    : '0,00';

  const chiffres = [
    {
      libelle: 'Comptes actifs',
      valeur: String(synthese.utilisateurs_actifs),
      detail: `sur ${synthese.comptes} ouvert${synthese.comptes > 1 ? 's' : ''}`,
    },
    {
      libelle: 'Productions générées',
      valeur: String(synthese.generations),
      detail: `${synthese.traces} accès tracés`,
    },
    {
      libelle: 'Dépense cumulée',
      valeur: `${synthese.cout_total.toFixed(2)} $`,
      detail:
        synthese.part_cache > 0
          ? `${synthese.part_cache} % servis par le cache`
          : 'depuis l’ouverture',
    },
    {
      libelle: 'Coût moyen / production',
      valeur: `${moyenne} $`,
      detail: `${(synthese.jetons_entree + synthese.jetons_sortie).toLocaleString('fr-FR')} jetons`,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="grid gap-2.5 [grid-template-columns:repeat(auto-fit,minmax(min(100%,190px),1fr))]">
        {chiffres.map((c) => (
          <div key={c.libelle} className="rounded-md border border-[#DDE2DD] bg-eg-blanc px-3 py-2.5">
            <div className="mb-1 text-[10.5px] uppercase tracking-[0.04em] text-eg-gris">
              {c.libelle}
            </div>
            <div className="text-[22px] font-semibold leading-none tracking-tight tabular-nums text-eg-encre">
              {c.valeur}
            </div>
            <div className="mt-1 text-[11px] text-eg-estompe">{c.detail}</div>
          </div>
        ))}
      </div>

      <div className="grid items-start gap-4 [grid-template-columns:repeat(auto-fit,minmax(min(100%,400px),1fr))]">
        <Section
          titre="Consommation par personne"
          precision={`${utilisateurs.length} compte${utilisateurs.length > 1 ? 's' : ''}`}
        >
          <div className="overflow-x-auto">
            <table className="min-w-[520px] w-full text-[12.5px]">
              <thead className={enTete}>
                <tr>
                  <th className="px-3 py-1.5 text-left font-semibold">Personne</th>
                  <th className="px-3 py-1.5 text-left font-semibold">Service</th>
                  <th className="px-3 py-1.5 text-right font-semibold">Prod.</th>
                  <th className="px-3 py-1.5 text-right font-semibold">Coût</th>
                </tr>
              </thead>
              <tbody>
                {utilisateurs.map((u) => (
                  <tr key={u.id} className="border-t border-eg-voile">
                    <td className="px-3 py-2">
                      <div className="text-eg-encre">{u.nom}</div>
                      <div className="text-[10.5px] text-eg-estompe">
                        {u.derniere_connexion
                          ? `vu le ${dateLisible(u.derniere_connexion)}`
                          : 'jamais connecté'}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-eg-gris">{u.service || '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-eg-gris">
                      {u.generations}
                    </td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums text-eg-encre">
                      {u.cout.toFixed(2)} $
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        <Section titre="Usage par assistant" precision="ouvertures et productions">
          <div className="overflow-x-auto">
            <table className="min-w-[420px] w-full text-[12.5px]">
              <thead className={enTete}>
                <tr>
                  <th className="px-3 py-1.5 text-left font-semibold">Assistant</th>
                  <th className="px-3 py-1.5 text-right font-semibold">Ouv.</th>
                  <th className="px-3 py-1.5 text-right font-semibold">Prod.</th>
                  <th className="px-3 py-1.5 text-right font-semibold">Coût</th>
                </tr>
              </thead>
              <tbody>
                {assistants.map((a) => (
                  <tr key={a.id} className="border-t border-eg-voile">
                    <td className="max-w-0 truncate px-3 py-2 text-eg-encre" title={a.titre}>
                      {a.titre}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-eg-gris">{a.acces}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-eg-gris">
                      {a.generations}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-eg-encre">
                      {a.cout.toFixed(2)} $
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      </div>

      <Section titre="Journal des accès" precision={`${traces.length} derniers`}>
        <div className="max-h-[26rem] overflow-auto">
          <table className="min-w-[640px] w-full text-[12.5px]">
            <thead className={`${enTete} sticky top-0`}>
              <tr>
                <th className="px-3 py-1.5 text-left font-semibold">Date et heure</th>
                <th className="px-3 py-1.5 text-left font-semibold">Personne</th>
                <th className="px-3 py-1.5 text-left font-semibold">Action</th>
                <th className="px-3 py-1.5 text-left font-semibold">Assistant</th>
                <th className="px-3 py-1.5 text-left font-semibold">Adresse</th>
              </tr>
            </thead>
            <tbody>
              {traces.map((t) => (
                <tr key={t.id} className="border-t border-eg-voile">
                  <td className="whitespace-nowrap px-3 py-1.5 tabular-nums text-eg-gris">
                    {dateLisible(t.date)}
                  </td>
                  <td className="px-3 py-1.5 text-eg-encre">{t.utilisateur}</td>
                  <td className="px-3 py-1.5 text-eg-gris">{t.action}</td>
                  <td className="max-w-0 truncate px-3 py-1.5 text-eg-estompe" title={t.assistant}>
                    {t.assistant || '—'}
                  </td>
                  <td className="px-3 py-1.5 font-mono text-[10.5px] text-eg-estompe">
                    {t.ip || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
};

export default SuiviUtilisation;
